import type { PluginSidebarProps } from "@d12labs/plugins/types";
import {
	BrandSquare,
	SidebarActionItem,
	SidebarBrand,
	SidebarNavItem,
	SidebarSection,
} from "@d12labs/ui/sidebar-kit";
import { useLocation, useNavigate } from "@tanstack/react-router";
import { BellDot, Inbox, LayoutGrid, Receipt } from "lucide-react";
import { useLedgerGaps, useLedgerSessions } from "./hooks";
import type { LedgerSearch } from "./overview";

export const SESSIONS_ANCHOR = "ledger-sessions";

function compact(n: number): string {
	return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toLocaleString("en-US");
}

/**
 * Ledger sidebar. Overview is the page; Sessions scrolls to its table; Data gaps
 * is its own page at /ledger/gaps.
 */
export const pluginSidebar = ({ onBack, onNavigate }: PluginSidebarProps) => {
	const navigate = useNavigate();
	const sessions = useLedgerSessions().data ?? [];
	const gaps = useLedgerGaps().data ?? [];
	const onOverview = useLocation({ select: (location) => location.pathname === "/ledger" });

	const toSessions = () => {
		onNavigate?.();
		// Keep the overview's filters when already there; the gaps page's
		// harness/q mean something else, so they must not leak across.
		navigate({
			to: "/ledger",
			search: (prev: LedgerSearch) => (onOverview ? prev : {}),
		}).then(() => document.getElementById(SESSIONS_ANCHOR)?.scrollIntoView({ behavior: "smooth" }));
	};

	return (
		<div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
			<SidebarBrand
				icon={<BrandSquare icon={Receipt} />}
				title="Ledger"
				subtitle="Session cost ledger"
				onBack={onBack}
			/>
			<div className="flex flex-col gap-1 px-2 py-2">
				<SidebarSection label="Workspace">
					<SidebarNavItem
						to="/ledger"
						exact
						icon={LayoutGrid}
						label="Overview"
						onNavigate={onNavigate}
					/>
					<SidebarActionItem
						icon={Inbox}
						label="Sessions"
						onClick={toSessions}
						trailing={
							sessions.length ? (
								<span className="tabular-nums text-ink-subtle">{compact(sessions.length)}</span>
							) : null
						}
					/>
					<SidebarNavItem
						to="/ledger/gaps"
						icon={BellDot}
						label="Data gaps"
						onNavigate={onNavigate}
						trailing={
							gaps.length ? (
								<span className="tabular-nums text-warning">{compact(gaps.length)}</span>
							) : null
						}
					/>
				</SidebarSection>
			</div>
		</div>
	);
};
